import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { ticketAPI } from '../lib/api';
import { toast } from 'sonner';
import { useVibration } from '../hooks/usePWA';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Ticket,
  Plus,
  Search,
  Filter,
  Clock,
  CheckCircle,
  AlertCircle,
  XCircle,
  MessageSquare,
  Paperclip,
  Send,
  User,
  Calendar,
  Tag,
  ArrowUpCircle,
  Loader2,
  TicketCheck,
  HelpCircle,
  Settings,
  FileText,
  Users,
  Building2,
} from 'lucide-react';

const HelpDesk = () => {
  const { user, isAdmin } = useAuth();
  const { vibrateSuccess, vibrateTap } = useVibration();
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [filters, setFilters] = useState({ status: '', category: '', priority: '' });
  const [searchQuery, setSearchQuery] = useState('');
  const [stats, setStats] = useState(null);

  // Form state
  const [newTicket, setNewTicket] = useState({
    subject: '',
    category: '',
    priority: 'medium',
    description: '',
  });
  const [newComment, setNewComment] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const categories = [
    { value: 'it_support', label: 'IT Support', icon: Settings },
    { value: 'hr_query', label: 'HR Query', icon: Users },
    { value: 'payroll', label: 'Payroll', icon: FileText },
    { value: 'leave', label: 'Leave Related', icon: Calendar },
    { value: 'facilities', label: 'Facilities', icon: Building2 },
    { value: 'other', label: 'Other', icon: HelpCircle },
  ];

  const priorities = [
    { value: 'low', label: 'Low', color: 'bg-slate-100 text-slate-700' },
    { value: 'medium', label: 'Medium', color: 'bg-blue-100 text-blue-700' },
    { value: 'high', label: 'High', color: 'bg-amber-100 text-amber-700' },
    { value: 'critical', label: 'Critical', color: 'bg-red-100 text-red-700' },
  ];

  const statuses = [
    { value: 'open', label: 'Open', color: 'bg-blue-100 text-blue-700', icon: AlertCircle },
    { value: 'in_progress', label: 'In Progress', color: 'bg-amber-100 text-amber-700', icon: Clock },
    { value: 'resolved', label: 'Resolved', color: 'bg-emerald-100 text-emerald-700', icon: CheckCircle },
    { value: 'closed', label: 'Closed', color: 'bg-slate-100 text-slate-700', icon: XCircle },
  ];

  useEffect(() => {
    fetchTickets();
    if (isAdmin()) {
      fetchStats();
    }
  }, [filters]);

  const fetchTickets = async () => {
    try {
      setLoading(true);
      const response = isAdmin()
        ? await ticketAPI.getAll(filters.status, filters.category, filters.priority)
        : await ticketAPI.getMyTickets(filters.status);
      setTickets(response.data);
    } catch (error) {
      toast.error('Failed to load tickets');
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await ticketAPI.getStats();
      setStats(response.data);
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    }
  };

  const handleCreateTicket = async () => {
    if (!newTicket.subject || !newTicket.category || !newTicket.description) {
      toast.error('Please fill in all required fields');
      return;
    }

    try {
      setSubmitting(true);
      vibrateTap();
      await ticketAPI.create(newTicket);
      vibrateSuccess();
      toast.success('Ticket created successfully');
      setShowCreateModal(false);
      setNewTicket({ subject: '', category: '', priority: 'medium', description: '' });
      fetchTickets();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create ticket');
    } finally {
      setSubmitting(false);
    }
  };

  const handleAddComment = async () => {
    if (!newComment.trim()) return;

    try {
      vibrateTap();
      await ticketAPI.addComment(selectedTicket.id, { content: newComment });
      vibrateSuccess();
      toast.success('Comment added');
      setNewComment('');
      // Refresh ticket details
      const response = await ticketAPI.getById(selectedTicket.id);
      setSelectedTicket(response.data);
      fetchTickets();
    } catch (error) {
      toast.error('Failed to add comment');
    }
  };

  const handleUpdateStatus = async (ticketId, status) => {
    try {
      vibrateTap();
      await ticketAPI.updateStatus(ticketId, status);
      vibrateSuccess();
      toast.success(`Ticket ${status}`);
      if (selectedTicket) {
        const response = await ticketAPI.getById(ticketId);
        setSelectedTicket(response.data);
      }
      fetchTickets();
      if (isAdmin()) fetchStats();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const handleAssignTicket = async (ticketId, assigneeId) => {
    try {
      await ticketAPI.assign(ticketId, assigneeId);
      toast.success('Ticket assigned');
      fetchTickets();
    } catch (error) {
      toast.error('Failed to assign ticket');
    }
  };

  const viewTicketDetails = async (ticket) => {
    try {
      const response = await ticketAPI.getById(ticket.id);
      setSelectedTicket(response.data);
      setShowDetailModal(true);
    } catch (error) {
      toast.error('Failed to load ticket details');
    }
  };

  const getStatusInfo = (status) => statuses.find(s => s.value === status) || statuses[0];
  const getPriorityInfo = (priority) => priorities.find(p => p.value === priority) || priorities[1];
  const getCategoryInfo = (category) => categories.find(c => c.value === category) || categories[5];

  const filteredTickets = tickets.filter(ticket =>
    ticket.subject.toLowerCase().includes(searchQuery.toLowerCase()) ||
    ticket.ticket_number.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="p-6 space-y-6" data-testid="help-desk-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
            <Ticket className="w-7 h-7 text-terra-500" />
            Help Desk
          </h1>
          <p className="text-slate-600 mt-1">
            {isAdmin() ? 'Manage and resolve employee tickets' : 'Submit and track your support requests'}
          </p>
        </div>
        <Button
          onClick={() => setShowCreateModal(true)}
          className="bg-terra-500 hover:bg-terra-600"
          data-testid="create-ticket-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          New Ticket
        </Button>
      </div>

      {/* Stats Cards (Admin only) - Clickable to filter */}
      {isAdmin() && stats && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <div 
            className={`bg-white rounded-xl border-2 p-4 cursor-pointer transition-all hover:shadow-lg hover:scale-[1.02] ${
              filters.status === 'open' ? 'border-blue-500 bg-blue-50 shadow-md' : 'border-slate-200 hover:border-blue-300'
            }`}
            onClick={() => setFilters({ ...filters, status: filters.status === 'open' ? '' : 'open' })}
            data-testid="stat-card-open"
          >
            <div className="flex items-center gap-3">
              <div className={`p-3 rounded-xl ${filters.status === 'open' ? 'bg-blue-500' : 'bg-blue-100'}`}>
                <AlertCircle className={`w-6 h-6 ${filters.status === 'open' ? 'text-white' : 'text-blue-600'}`} />
              </div>
              <div>
                <p className="text-3xl font-bold text-slate-800">{stats.open || 0}</p>
                <p className="text-sm font-medium text-slate-500">Open</p>
              </div>
            </div>
            {filters.status === 'open' && (
              <p className="text-xs text-blue-600 mt-2 font-medium">Click to clear filter</p>
            )}
          </div>
          
          <div 
            className={`bg-white rounded-xl border-2 p-4 cursor-pointer transition-all hover:shadow-lg hover:scale-[1.02] ${
              filters.status === 'in_progress' ? 'border-amber-500 bg-amber-50 shadow-md' : 'border-slate-200 hover:border-amber-300'
            }`}
            onClick={() => setFilters({ ...filters, status: filters.status === 'in_progress' ? '' : 'in_progress' })}
            data-testid="stat-card-in-progress"
          >
            <div className="flex items-center gap-3">
              <div className={`p-3 rounded-xl ${filters.status === 'in_progress' ? 'bg-amber-500' : 'bg-amber-100'}`}>
                <Clock className={`w-6 h-6 ${filters.status === 'in_progress' ? 'text-white' : 'text-amber-600'}`} />
              </div>
              <div>
                <p className="text-3xl font-bold text-slate-800">{stats.in_progress || 0}</p>
                <p className="text-sm font-medium text-slate-500">In Progress</p>
              </div>
            </div>
            {filters.status === 'in_progress' && (
              <p className="text-xs text-amber-600 mt-2 font-medium">Click to clear filter</p>
            )}
          </div>
          
          <div 
            className={`bg-white rounded-xl border-2 p-4 cursor-pointer transition-all hover:shadow-lg hover:scale-[1.02] ${
              filters.status === 'resolved' ? 'border-emerald-500 bg-emerald-50 shadow-md' : 'border-slate-200 hover:border-emerald-300'
            }`}
            onClick={() => setFilters({ ...filters, status: filters.status === 'resolved' ? '' : 'resolved' })}
            data-testid="stat-card-resolved"
          >
            <div className="flex items-center gap-3">
              <div className={`p-3 rounded-xl ${filters.status === 'resolved' ? 'bg-emerald-500' : 'bg-emerald-100'}`}>
                <CheckCircle className={`w-6 h-6 ${filters.status === 'resolved' ? 'text-white' : 'text-emerald-600'}`} />
              </div>
              <div>
                <p className="text-3xl font-bold text-slate-800">{stats.resolved || 0}</p>
                <p className="text-sm font-medium text-slate-500">Resolved</p>
              </div>
            </div>
            {filters.status === 'resolved' && (
              <p className="text-xs text-emerald-600 mt-2 font-medium">Click to clear filter</p>
            )}
          </div>
          
          <div 
            className={`bg-white rounded-xl border-2 p-4 cursor-pointer transition-all hover:shadow-lg hover:scale-[1.02] ${
              filters.priority === 'critical' ? 'border-red-500 bg-red-50 shadow-md' : 'border-slate-200 hover:border-red-300'
            }`}
            onClick={() => setFilters({ ...filters, priority: filters.priority === 'critical' ? '' : 'critical' })}
            data-testid="stat-card-critical"
          >
            <div className="flex items-center gap-3">
              <div className={`p-3 rounded-xl ${filters.priority === 'critical' ? 'bg-red-500' : 'bg-red-100'}`}>
                <ArrowUpCircle className={`w-6 h-6 ${filters.priority === 'critical' ? 'text-white' : 'text-red-600'}`} />
              </div>
              <div>
                <p className="text-3xl font-bold text-slate-800">{stats.critical || 0}</p>
                <p className="text-sm font-medium text-slate-500">Critical</p>
              </div>
            </div>
            {filters.priority === 'critical' && (
              <p className="text-xs text-red-600 mt-2 font-medium">Click to clear filter</p>
            )}
          </div>
          
          <div 
            className={`bg-white rounded-xl border-2 p-4 cursor-pointer transition-all hover:shadow-lg hover:scale-[1.02] ${
              filters.status === 'closed' ? 'border-slate-500 bg-slate-100 shadow-md' : 'border-slate-200 hover:border-slate-400'
            }`}
            onClick={() => setFilters({ ...filters, status: filters.status === 'closed' ? '' : 'closed' })}
            data-testid="stat-card-closed"
          >
            <div className="flex items-center gap-3">
              <div className={`p-3 rounded-xl ${filters.status === 'closed' ? 'bg-slate-500' : 'bg-slate-200'}`}>
                <XCircle className={`w-6 h-6 ${filters.status === 'closed' ? 'text-white' : 'text-slate-600'}`} />
              </div>
              <div>
                <p className="text-3xl font-bold text-slate-800">{stats.closed || 0}</p>
                <p className="text-sm font-medium text-slate-500">Closed</p>
              </div>
            </div>
            {filters.status === 'closed' && (
              <p className="text-xs text-slate-600 mt-2 font-medium">Click to clear filter</p>
            )}
          </div>
        </div>
      )}

      {/* Active Filter Indicator */}
      {(filters.status || filters.category || filters.priority) && (
        <div className="flex items-center gap-2 bg-terra-50 border border-terra-200 rounded-lg px-4 py-2">
          <Filter className="w-4 h-4 text-terra-600" />
          <span className="text-sm text-terra-700 font-medium">
            Filtering by: 
            {filters.status && <Badge variant="outline" className="ml-2 capitalize">{filters.status.replace('_', ' ')}</Badge>}
            {filters.category && <Badge variant="outline" className="ml-2 capitalize">{filters.category.replace('_', ' ')}</Badge>}
            {filters.priority && <Badge variant="outline" className="ml-2 capitalize">{filters.priority}</Badge>}
          </span>
          <Button 
            variant="ghost" 
            size="sm" 
            className="ml-auto text-terra-600 hover:text-terra-800"
            onClick={() => setFilters({ status: '', category: '', priority: '' })}
          >
            Clear All Filters
          </Button>
        </div>
      )}

      {/* Filters */}
      <div className="bg-white rounded-xl border border-slate-200 p-4">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              placeholder="Search by ticket number or subject..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9"
              data-testid="search-tickets-input"
            />
          </div>
          <div className="flex gap-2">
            <Select value={filters.status || "all"} onValueChange={(v) => setFilters({ ...filters, status: v === "all" ? "" : v })}>
              <SelectTrigger className="w-36" data-testid="filter-status">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                {statuses.map((s) => (
                  <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={filters.category || "all"} onValueChange={(v) => setFilters({ ...filters, category: v === "all" ? "" : v })}>
              <SelectTrigger className="w-36" data-testid="filter-category">
                <SelectValue placeholder="Category" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Categories</SelectItem>
                {categories.map((c) => (
                  <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            {isAdmin() && (
              <Select value={filters.priority || "all"} onValueChange={(v) => setFilters({ ...filters, priority: v === "all" ? "" : v })}>
                <SelectTrigger className="w-32" data-testid="filter-priority">
                  <SelectValue placeholder="Priority" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Priority</SelectItem>
                  {priorities.map((p) => (
                    <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>
        </div>
      </div>

      {/* Tickets List */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-terra-500" />
          </div>
        ) : filteredTickets.length === 0 ? (
          <div className="text-center py-12">
            <TicketCheck className="w-12 h-12 text-slate-300 mx-auto mb-3" />
            <p className="text-slate-500">No tickets found</p>
            <Button
              variant="link"
              onClick={() => setShowCreateModal(true)}
              className="text-terra-500 mt-2"
            >
              Create your first ticket
            </Button>
          </div>
        ) : (
          <div className="divide-y divide-slate-100">
            {filteredTickets.map((ticket) => {
              const statusInfo = getStatusInfo(ticket.status);
              const priorityInfo = getPriorityInfo(ticket.priority);
              const categoryInfo = getCategoryInfo(ticket.category);
              const CategoryIcon = categoryInfo.icon;

              return (
                <div
                  key={ticket.id}
                  className="p-4 hover:bg-slate-50 transition-colors cursor-pointer"
                  onClick={() => viewTicketDetails(ticket)}
                  data-testid={`ticket-row-${ticket.id}`}
                >
                  <div className="flex items-start gap-4">
                    <div className={`p-2 rounded-lg ${priorityInfo.color}`}>
                      <CategoryIcon className="w-5 h-5" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-xs font-mono text-slate-400">
                          {ticket.ticket_number}
                        </span>
                        <Badge className={statusInfo.color}>
                          {statusInfo.label}
                        </Badge>
                        <Badge className={priorityInfo.color}>
                          {priorityInfo.label}
                        </Badge>
                      </div>
                      <h3 className="font-medium text-slate-800 truncate">
                        {ticket.subject}
                      </h3>
                      <div className="flex items-center gap-4 mt-2 text-xs text-slate-500">
                        <span className="flex items-center gap-1">
                          <User className="w-3 h-3" />
                          {ticket.created_by_name || 'Unknown'}
                        </span>
                        <span className="flex items-center gap-1">
                          <Calendar className="w-3 h-3" />
                          {new Date(ticket.created_at).toLocaleDateString()}
                        </span>
                        <span className="flex items-center gap-1">
                          <MessageSquare className="w-3 h-3" />
                          {ticket.comments_count || 0} comments
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Create Ticket Modal */}
      <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Plus className="w-5 h-5 text-terra-500" />
              Create New Ticket
            </DialogTitle>
            <DialogDescription>
              Submit a support request. Our team will respond as soon as possible.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <label className="text-sm font-medium text-slate-700 mb-1.5 block">
                Subject <span className="text-red-500">*</span>
              </label>
              <Input
                placeholder="Brief description of your issue"
                value={newTicket.subject}
                onChange={(e) => setNewTicket({ ...newTicket, subject: e.target.value })}
                data-testid="ticket-subject-input"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-slate-700 mb-1.5 block">
                  Category <span className="text-red-500">*</span>
                </label>
                <Select
                  value={newTicket.category}
                  onValueChange={(v) => setNewTicket({ ...newTicket, category: v })}
                >
                  <SelectTrigger data-testid="ticket-category-select">
                    <SelectValue placeholder="Select category" />
                  </SelectTrigger>
                  <SelectContent>
                    {categories.map((c) => (
                      <SelectItem key={c.value} value={c.value}>
                        <span className="flex items-center gap-2">
                          <c.icon className="w-4 h-4" />
                          {c.label}
                        </span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700 mb-1.5 block">
                  Priority
                </label>
                <Select
                  value={newTicket.priority}
                  onValueChange={(v) => setNewTicket({ ...newTicket, priority: v })}
                >
                  <SelectTrigger data-testid="ticket-priority-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {priorities.map((p) => (
                      <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700 mb-1.5 block">
                Description <span className="text-red-500">*</span>
              </label>
              <textarea
                placeholder="Please provide detailed information about your issue..."
                value={newTicket.description}
                onChange={(e) => setNewTicket({ ...newTicket, description: e.target.value })}
                className="w-full min-h-[120px] px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-terra-500 focus:border-transparent resize-none"
                data-testid="ticket-description-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateModal(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleCreateTicket}
              disabled={submitting}
              className="bg-terra-500 hover:bg-terra-600"
              data-testid="submit-ticket-btn"
            >
              {submitting ? (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              ) : (
                <Send className="w-4 h-4 mr-2" />
              )}
              Submit Ticket
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Ticket Detail Modal */}
      <Dialog open={showDetailModal} onOpenChange={setShowDetailModal}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          {selectedTicket && (
            <>
              <DialogHeader>
                <div className="flex items-center gap-2 text-xs text-slate-500 mb-1">
                  <span className="font-mono">{selectedTicket.ticket_number}</span>
                  <Badge className={getStatusInfo(selectedTicket.status).color}>
                    {getStatusInfo(selectedTicket.status).label}
                  </Badge>
                </div>
                <DialogTitle>{selectedTicket.subject}</DialogTitle>
              </DialogHeader>

              <div className="space-y-4 py-4">
                {/* Ticket Info */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 p-4 bg-slate-50 rounded-lg">
                  <div>
                    <p className="text-xs text-slate-500">Category</p>
                    <p className="text-sm font-medium">{getCategoryInfo(selectedTicket.category).label}</p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500">Priority</p>
                    <Badge className={getPriorityInfo(selectedTicket.priority).color}>
                      {getPriorityInfo(selectedTicket.priority).label}
                    </Badge>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500">Created</p>
                    <p className="text-sm">{new Date(selectedTicket.created_at).toLocaleString()}</p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500">By</p>
                    <p className="text-sm">{selectedTicket.created_by_name}</p>
                  </div>
                </div>

                {/* Description */}
                <div>
                  <h4 className="text-sm font-medium text-slate-700 mb-2">Description</h4>
                  <p className="text-sm text-slate-600 whitespace-pre-wrap bg-white border border-slate-200 rounded-lg p-4">
                    {selectedTicket.description}
                  </p>
                </div>

                {/* Admin Actions */}
                {isAdmin() && selectedTicket.status !== 'closed' && (
                  <div className="flex gap-2 p-4 bg-slate-50 rounded-lg">
                    <span className="text-sm text-slate-600 mr-2">Update Status:</span>
                    {selectedTicket.status === 'open' && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleUpdateStatus(selectedTicket.id, 'in_progress')}
                        data-testid="set-in-progress-btn"
                      >
                        <Clock className="w-3 h-3 mr-1" />
                        Start Progress
                      </Button>
                    )}
                    {(selectedTicket.status === 'open' || selectedTicket.status === 'in_progress') && (
                      <Button
                        size="sm"
                        variant="outline"
                        className="text-emerald-600 border-emerald-300 hover:bg-emerald-50"
                        onClick={() => handleUpdateStatus(selectedTicket.id, 'resolved')}
                        data-testid="resolve-ticket-btn"
                      >
                        <CheckCircle className="w-3 h-3 mr-1" />
                        Resolve
                      </Button>
                    )}
                    {selectedTicket.status === 'resolved' && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleUpdateStatus(selectedTicket.id, 'closed')}
                        data-testid="close-ticket-btn"
                      >
                        <XCircle className="w-3 h-3 mr-1" />
                        Close
                      </Button>
                    )}
                  </div>
                )}

                {/* Comments */}
                <div>
                  <h4 className="text-sm font-medium text-slate-700 mb-3 flex items-center gap-2">
                    <MessageSquare className="w-4 h-4" />
                    Comments ({selectedTicket.comments?.length || 0})
                  </h4>
                  <div className="space-y-3 max-h-60 overflow-y-auto">
                    {selectedTicket.comments?.length === 0 ? (
                      <p className="text-sm text-slate-400 text-center py-4">No comments yet</p>
                    ) : (
                      selectedTicket.comments?.map((comment, idx) => (
                        <div
                          key={idx}
                          className={`p-3 rounded-lg ${
                            comment.user_role === 'admin' || comment.user_role === 'hr'
                              ? 'bg-terra-50 border border-terra-100'
                              : 'bg-slate-50 border border-slate-100'
                          }`}
                        >
                          <div className="flex items-center gap-2 mb-1">
                            <span className="text-sm font-medium">{comment.user_name}</span>
                            <Badge variant="outline" className="text-[10px]">
                              {comment.user_role}
                            </Badge>
                            <span className="text-xs text-slate-400 ml-auto">
                              {new Date(comment.created_at).toLocaleString()}
                            </span>
                          </div>
                          <p className="text-sm text-slate-600">{comment.content}</p>
                        </div>
                      ))
                    )}
                  </div>

                  {/* Add Comment */}
                  {selectedTicket.status !== 'closed' && (
                    <div className="flex gap-2 mt-4">
                      <Input
                        placeholder="Add a comment..."
                        value={newComment}
                        onChange={(e) => setNewComment(e.target.value)}
                        onKeyPress={(e) => e.key === 'Enter' && handleAddComment()}
                        data-testid="comment-input"
                      />
                      <Button
                        onClick={handleAddComment}
                        disabled={!newComment.trim()}
                        className="bg-terra-500 hover:bg-terra-600"
                        data-testid="add-comment-btn"
                      >
                        <Send className="w-4 h-4" />
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default HelpDesk;
